import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';

export class BmhComboZoom extends Component {
  requiredRefs = ['dialog', 'stage', 'image', 'media', 'thumbnails', 'counter', 'scaleLabel'];
  #controller;
  #resize;
  #pointers = new Map();
  #index = 0;
  #scale = 1;
  #x = 0;
  #y = 0;
  #gesture;
  #tap = null;
  #lastTouch = -Infinity;
  #frame = 0;
  #request = 0;
  #loader;
  #opener;
  #animation;
  #box = { width: 1, height: 1, left: 0, top: 0 };

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const stage = this.refs.stage;
    stage.addEventListener('pointerdown', this.#down, { signal });
    stage.addEventListener('pointermove', this.#move, { signal });
    stage.addEventListener('pointerup', this.#up, { signal });
    stage.addEventListener('pointercancel', this.#cancelPointer, { signal });
    stage.addEventListener('wheel', this.#wheel, { passive: false, signal });
    stage.addEventListener('dblclick', this.#doubleClick, { signal });
    this.refs.dialog.addEventListener('keydown', this.#key, { signal });
    this.refs.dialog.addEventListener('close', this.#closed, { signal });
    this.#resize = new ResizeObserver(() => {
      if (!this.refs.dialog.open) return;
      this.#measure();
      this.#pointers.clear();
      this.reset();
    });
    this.#resize.observe(stage);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resize?.disconnect();
    this.#request++;
    this.#animation?.cancel();
    cancelAnimationFrame(this.#frame);
    if (this.#loader) this.#loader.onload = null;
    this.#pointers.clear();
    this.refs.dialog?.close();
    super.disconnectedCallback();
  }

  open(index, event) {
    event?.preventDefault();
    if (this.refs.dialog.open) return;
    this.#opener = document.activeElement;
    this.refs.dialog.showModal();
    this.#measure();
    this.select(index);
    this.refs.closeButton.focus({ preventScroll: true });
  }

  close() { this.refs.dialog.close(); }

  #closed = () => {
    this.#request++;
    this.#pointers.clear();
    this.#tap = null;
    this.reset();
    this.#opener?.focus({ preventScroll: true });
  };

  select(galleryIndex) {
    const index = this.refs.media.findIndex(item => Number(item.dataset.galleryIndex) === Number(galleryIndex));
    if (index < 0) return;
    this.#index = index;
    const item = this.refs.media[index];
    const image = this.refs.image;
    const request = ++this.#request;
    this.#pointers.clear();
    this.#tap = null;
    this.reset();
    image.alt = item.dataset.alt || '';
    image.width = Number(item.dataset.width) || 1200;
    image.height = Number(item.dataset.height) || 1200;
    image.src = item.dataset.preview;
    this.refs.counter.textContent = `${index + 1} / ${this.refs.media.length}`;
    this.refs.previous.disabled = index === 0;
    this.refs.next.disabled = index === this.refs.media.length - 1;
    for (const [i, button] of this.refs.media.entries()) button.setAttribute('aria-pressed', String(i === index));
    const rail = this.refs.thumbnails;
    rail.scrollTo({ left: item.offsetLeft - rail.offsetLeft - (rail.clientWidth - item.clientWidth) / 2, behavior: 'instant' });
    if (this.#loader) this.#loader.onload = null;
    const full = new Image();
    this.#loader = full;
    full.decoding = 'async';
    full.onload = async () => {
      try { await full.decode(); } catch {}
      if (request === this.#request && this.isConnected && this.refs.dialog.open) image.src = full.src;
    };
    full.src = item.dataset.full;
    this.dispatchEvent(new ZoomMediaSelectedEvent(Number(item.dataset.galleryIndex)));
  }

  previous() { this.#go(-1); }
  next() { this.#go(1); }
  #go(direction) {
    const item = this.refs.media[this.#index + direction];
    if (item) this.select(item.dataset.galleryIndex);
  }
  zoomIn() { this.#zoom(this.#scale + .5); }
  zoomOut() { this.#zoom(this.#scale - .5); }
  reset() {
    this.#animation?.cancel();
    cancelAnimationFrame(this.#frame); this.#frame = 0;
    this.#scale = 1; this.#x = 0; this.#y = 0; this.#render();
  }
  loadHighResolutionImage() {}

  #measure() {
    const box = this.refs.stage.getBoundingClientRect();
    this.#box = { width: box.width, height: box.height, left: box.left, top: box.top };
  }

  #zoom(next, point) {
    const target = Math.max(1, Math.min(4, next));
    const ratio = target / this.#scale;
    const px = point ? point.x - this.#box.left - this.#box.width / 2 : 0;
    const py = point ? point.y - this.#box.top - this.#box.height / 2 : 0;
    const old = this.refs.image.style.transform;
    this.#x = px - (px - this.#x) * ratio;
    this.#y = py - (py - this.#y) * ratio;
    this.#scale = target;
    this.#render();
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#animation?.cancel();
      this.#animation = this.refs.image.animate([{ transform: old || 'none' }, { transform: this.refs.image.style.transform }], { duration: 240, easing: 'cubic-bezier(.22,1,.36,1)' });
    }
  }

  #render() {
    const item = this.refs.media[this.#index];
    if (!item) return;
    const { width, height } = this.#box;
    const ratio = Number(item.dataset.width) / Number(item.dataset.height) || 1;
    const fittedWidth = Math.min(width, height * ratio);
    const fittedHeight = Math.min(height, width / ratio);
    const maxX = Math.max(0, (fittedWidth * this.#scale - width) / 2);
    const maxY = Math.max(0, (fittedHeight * this.#scale - height) / 2);
    this.#x = Math.max(-maxX, Math.min(maxX, this.#x));
    this.#y = Math.max(-maxY, Math.min(maxY, this.#y));
    this.refs.image.style.transform = `translate3d(${this.#x}px,${this.#y}px,0) scale(${this.#scale})`;
    this.refs.scaleLabel.textContent = `${Math.round(this.#scale * 10) / 10}×`;
    this.refs.plus.disabled = this.#scale >= 4;
    this.refs.minus.disabled = this.#scale <= 1;
    this.refs.stage.dataset.zoomed = String(this.#scale > 1);
  }

  #startGesture() {
    const points = [...this.#pointers.values()];
    const a = points[0];
    if (!a) return;
    const b = points[1];
    this.#gesture = {
      start: a, last: a, moved: false, pinched: !!b, scale: this.#scale, x: this.#x, y: this.#y,
      distance: b ? Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)) : 1,
      center: b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : a,
    };
  }

  #down = event => {
    if (event.button !== 0) return;
    if (event.pointerType === 'touch') this.#lastTouch = performance.now();
    this.#animation?.cancel();
    this.#measure();
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.refs.stage.setPointerCapture(event.pointerId);
    this.refs.stage.dataset.dragging = 'true';
    this.#startGesture();
  };

  #move = event => {
    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...this.#pointers.values()];
    const g = this.#gesture;
    if (points.length > 1) {
      const [a, b] = points;
      const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const scale = Math.max(1, Math.min(4, g.scale * Math.hypot(b.x - a.x, b.y - a.y) / g.distance));
      const ratio = scale / g.scale;
      const localX = g.center.x - this.#box.left - this.#box.width / 2;
      const localY = g.center.y - this.#box.top - this.#box.height / 2;
      this.#x = localX - (localX - g.x) * ratio + center.x - g.center.x;
      this.#y = localY - (localY - g.y) * ratio + center.y - g.center.y;
      this.#scale = scale;
      g.moved = true;
    } else {
      const point = points[0];
      g.last = point;
      const dx = point.x - g.start.x;
      const dy = point.y - g.start.y;
      if (Math.hypot(dx, dy) > 8) g.moved = true;
      if (this.#scale > 1) { this.#x = g.x + dx; this.#y = g.y + dy; }
    }
    if (!this.#frame) this.#frame = requestAnimationFrame(() => { this.#frame = 0; this.#render(); });
  };

  #up = event => {
    if (!this.#pointers.has(event.pointerId)) return;
    const g = this.#gesture;
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size) { this.#startGesture(); this.#gesture.pinched = true; return; }
    this.refs.stage.dataset.dragging = 'false';
    if (!g || g.pinched) return;
    const point = { x: event.clientX, y: event.clientY };
    const dx = point.x - g.start.x;
    const dy = point.y - g.start.y;
    if (g.moved) {
      this.#tap = null;
      if (this.#scale === 1 && Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.3) this.#go(dx < 0 ? 1 : -1);
      return;
    }
    if (event.pointerType !== 'touch') return;
    const now = performance.now();
    if (this.#tap && now - this.#tap.time < 300 && Math.hypot(point.x - this.#tap.x, point.y - this.#tap.y) < 35) {
      this.#zoom(this.#scale > 1 ? 1 : 2.5, point);
      this.#tap = null;
    } else this.#tap = { ...point, time: now };
  };

  #cancelPointer = event => { this.#pointers.delete(event.pointerId); this.#tap = null; this.#gesture = null; this.refs.stage.dataset.dragging = 'false'; };
  #doubleClick = event => {
    // Touch double taps may also emit a synthetic mouse dblclick in mobile browsers.
    if (event.pointerType === 'touch' || performance.now() - this.#lastTouch < 600) return;
    this.#zoom(this.#scale > 1 ? 1 : 2.5, { x: event.clientX, y: event.clientY });
  };
  #wheel = event => { event.preventDefault(); this.#zoom(this.#scale * Math.exp(-event.deltaY * .002), { x: event.clientX, y: event.clientY }); };
  #key = event => {
    if (event.key === '+') { event.preventDefault(); this.zoomIn(); }
    else if (event.key === '-') { event.preventDefault(); this.zoomOut(); }
    else if (event.key === '0') { event.preventDefault(); this.reset(); }
    else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.#go(event.key === 'ArrowRight' ? 1 : -1); }
  };
}

if (!customElements.get('bmh-combo-zoom')) customElements.define('bmh-combo-zoom', BmhComboZoom);
